import jsPDF from 'jspdf';
import { SECTION_TITLES, FORM_SECTIONS } from './constants';

export const generatePDF = (formData) => {
  const doc = new jsPDF();
  let yPosition = 20;
  const lineHeight = 7;
  const sectionSpacing = 15;
  const margin = 20;
  const pageWidth = doc.internal.pageSize.width;
  const contentWidth = pageWidth - (2 * margin);

  // Helper function to add text with word wrapping
  const addWrappedText = (text, x, y, maxWidth) => {
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    return lines.length * lineHeight;
  };

  // Helper function to add section header
  const addSectionHeader = (title, y) => {
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(title, margin, y);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    return y + lineHeight + 5;
  };

  // Helper function to add field
  const addField = (label, value, y) => {
    if (value !== undefined && value !== null && value !== '') {
      const text = `${label}: ${value}`;
      const height = addWrappedText(text, margin, y, contentWidth);
      return y + height + 2;
    }
    return y;
  };

  // Helper function to add conditional field
  const addConditionalField = (condition, label, value, y) => {
    if (condition && value !== undefined && value !== null && value !== '') {
      return addField(label, value, y);
    }
    return y;
  };

  // Title
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('2025 Tax Return Checklist', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 20;

  // Date
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  const currentDate = new Date().toLocaleDateString('en-AU');
  yPosition = addField('Date Prepared', currentDate, yPosition);
  yPosition += sectionSpacing;

  // Check if we need a new page
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  // Section A: Client Details
  yPosition = addSectionHeader(`Section ${FORM_SECTIONS.CLIENT_DETAILS}: ${SECTION_TITLES[FORM_SECTIONS.CLIENT_DETAILS]}`, yPosition);
  
  const client = formData[FORM_SECTIONS.CLIENT_DETAILS] || {};
  yPosition = addField('Name', `${client.firstName || ''} ${client.lastName || ''}`.trim(), yPosition);
  yPosition = addField('Email', client.email, yPosition);
  yPosition = addField('Phone', client.phone, yPosition);
  yPosition = addField('Address', `${client.address || ''}, ${client.suburb || ''}, ${client.state || ''} ${client.postcode || ''}`.trim(), yPosition);
  
  if (client.isNewClient) {
    yPosition = addField('TFN', client.tfn, yPosition);
    yPosition = addField('Bank Name', client.bankName, yPosition);
    yPosition = addField('BSB', client.bsb, yPosition);
    yPosition = addField('Account Number', client.accountNumber, yPosition);
    yPosition = addField('Account Name', client.accountName, yPosition);
  }
  yPosition += sectionSpacing;

  // Check if we need a new page
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  // Section B: Income
  yPosition = addSectionHeader(`Section ${FORM_SECTIONS.INCOME}: ${SECTION_TITLES[FORM_SECTIONS.INCOME]}`, yPosition);
  
  const income = formData[FORM_SECTIONS.INCOME] || {};
  yPosition = addConditionalField(income.hasSalaryWages, 'ITEM 1 - Salary or Wages', 'YES', yPosition);
  yPosition = addConditionalField(income.hasAllowancesTips, 'ITEM 2 - Allowances, Tips, Directors Fees, Earnings', 'YES', yPosition);
  yPosition = addConditionalField(income.hasEmployerLumpSum, 'ITEM 3 - Employer Lump Sum Payments', 'YES', yPosition);
  yPosition = addConditionalField(income.hasEmploymentTermination, 'ITEM 4 - Employment Termination Payments', 'YES', yPosition);
  yPosition = addConditionalField(income.hasAustGovtAllowances, 'ITEM 5 - Australian Govt Allowances & Payments', 'YES', yPosition);
  yPosition = addConditionalField(income.hasAustGovtPensions, 'ITEM 6 - Aust Gov Pensions and Allowances', 'YES', yPosition);
  yPosition = addConditionalField(income.hasAustAnnuitiesSuper, 'ITEM 7 - Aust Annuities & Super Income Streams', 'YES', yPosition);
  yPosition = addConditionalField(income.hasAustSuperLumpSum, 'ITEM 8 - Australian Super Lump Sum Payments', 'YES', yPosition);
  yPosition = addConditionalField(income.hasAttributedPersonalServices, 'ITEM 9 - Attributed Personal Services Income', 'YES', yPosition);
  yPosition = addConditionalField(income.hasInterest, 'ITEM 10 - Interest Received', 'YES', yPosition);
  yPosition = addConditionalField(income.hasDividends, 'ITEM 11 - Dividends', 'YES', yPosition);
  yPosition = addConditionalField(income.hasEmployeeShareScheme, 'ITEM 12 - Employee Share Scheme', 'YES', yPosition);
  yPosition = addConditionalField(income.hasPartnershipsTrusts, 'ITEM 13 - Partnerships/Trusts', 'YES', yPosition);
  yPosition = addConditionalField(income.hasCapitalGains, 'ITEM 18 - Sale of Assets (Capital Gains Tax)', 'YES', yPosition);
  yPosition = addConditionalField(income.hasForeignIncome, 'ITEM 20 - Foreign Income and/or Foreign Tax Credits', 'YES', yPosition);
  yPosition = addConditionalField(income.hasRental, 'ITEM 21 - Rental Income and Expenses', 'YES', yPosition);
  yPosition = addConditionalField(income.hasOtherIncome, 'ITEM 24 - Any Other Income', 'YES', yPosition);
  yPosition += sectionSpacing;

  // Check if we need a new page
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  // Section C: Deductions
  yPosition = addSectionHeader(`Section ${FORM_SECTIONS.DEDUCTIONS}: ${SECTION_TITLES[FORM_SECTIONS.DEDUCTIONS]}`, yPosition);
  
  const deductions = formData[FORM_SECTIONS.DEDUCTIONS] || {};
  if (deductions.hasVehicle) {
    yPosition = addField('Vehicle Method', deductions.vehicleMethod, yPosition);
    if (deductions.vehicleMethod === 'Logbook Method (must be completed every 5 years)') {
      yPosition = addField('Logbook Percentage', `${deductions.logbookPercentage}%`, yPosition);
    } else if (deductions.vehicleMethod === 'Cents per Kilometre Method (85 cents per KM from 1 July 2024)') {
      yPosition = addField('Kilometres', deductions.kilometres, yPosition);
    }
  }
  yPosition = addConditionalField(deductions.hasClothing, 'Work Clothing', deductions.clothingAmount ? `$${deductions.clothingAmount.toLocaleString()}` : '', yPosition);
  yPosition = addConditionalField(deductions.hasEducation, 'Education & Training', deductions.educationAmount ? `$${deductions.educationAmount.toLocaleString()}` : '', yPosition);
  yPosition = addConditionalField(deductions.hasMobile, 'Mobile & Internet', deductions.mobileAmount ? `$${deductions.mobileAmount.toLocaleString()}` : '', yPosition);
  yPosition = addConditionalField(deductions.hasMobile, 'Mobile Usage %', deductions.mobilePercentage ? `${deductions.mobilePercentage}%` : '', yPosition);
  yPosition = addConditionalField(deductions.hasHomeOffice, 'Home Office Hours', deductions.homeOfficeHours, yPosition);
  yPosition = addConditionalField(deductions.hasHomeOffice, 'Home Office Rate', deductions.homeOfficeRate, yPosition);
  yPosition = addConditionalField(deductions.hasTools, 'Tools & Equipment', deductions.toolsAmount ? `$${deductions.toolsAmount.toLocaleString()}` : '', yPosition);
  yPosition = addConditionalField(deductions.hasProfessional, 'Professional Memberships', deductions.professionalAmount ? `$${deductions.professionalAmount.toLocaleString()}` : '', yPosition);
  yPosition = addConditionalField(deductions.hasOtherDeductions, 'Other Deductions', deductions.otherDeductionsAmount ? `$${deductions.otherDeductionsAmount.toLocaleString()}` : '', yPosition);
  yPosition = addConditionalField(deductions.hasOtherDeductions, 'Other Deductions Description', deductions.otherDeductionsDescription, yPosition);
  yPosition += sectionSpacing;

  // Check if we need a new page
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  // Section D: Tax Offsets & Health
  yPosition = addSectionHeader(`Section ${FORM_SECTIONS.TAX_OFFSETS}: ${SECTION_TITLES[FORM_SECTIONS.TAX_OFFSETS]}`, yPosition);
  
  const taxOffsets = formData[FORM_SECTIONS.TAX_OFFSETS] || {};
  yPosition = addField('Tax Offset', taxOffsets.taxOffset, yPosition);
  yPosition = addConditionalField(taxOffsets.hasPrivateHealth, 'Private Health Cover Code', taxOffsets.healthCoverCode, yPosition);
  yPosition = addField('Dependent Children', taxOffsets.dependentChildren, yPosition);
  yPosition = addField('Marital Status', taxOffsets.isMarried ? 'Married/De Facto' : 'Single', yPosition);
  yPosition += sectionSpacing;

  // Check if we need a new page
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  // Section E: Spouse Info (if applicable)
  if (taxOffsets.isMarried) {
    yPosition = addSectionHeader(`Section ${FORM_SECTIONS.SPOUSE_INFO}: ${SECTION_TITLES[FORM_SECTIONS.SPOUSE_INFO]}`, yPosition);
    
    const spouse = formData[FORM_SECTIONS.SPOUSE_INFO] || {};
    yPosition = addField('Spouse Name', `${spouse.spouseFirstName || ''} ${spouse.spouseLastName || ''}`.trim(), yPosition);
    yPosition = addField('Spouse Income', spouse.spouseIncome ? `$${spouse.spouseIncome.toLocaleString()}` : '', yPosition);
    yPosition = addField('Spouse Fringe Benefits', spouse.spouseFringeBenefits ? `$${spouse.spouseFringeBenefits.toLocaleString()}` : '', yPosition);
    yPosition = addField('Spouse Rental Losses', spouse.spouseRentalLosses ? `$${spouse.spouseRentalLosses.toLocaleString()}` : '', yPosition);
    yPosition = addField('Spouse Government Pensions', spouse.spouseGovernmentPensions ? `$${spouse.spouseGovernmentPensions.toLocaleString()}` : '', yPosition);
    yPosition += sectionSpacing;
  }

  // Footer
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  doc.setFontSize(8);
  doc.text('This checklist is for preparation purposes only. Please consult with your tax professional for final advice.', margin, yPosition);
  yPosition += 10;
  doc.text('Generated on: ' + new Date().toLocaleString('en-AU'), margin, yPosition);

  return doc;
};

export const downloadPDF = (formData, filename = 'TaxChecklist2025.pdf') => {
  const doc = generatePDF(formData);
  doc.save(filename);
}; 